import { html, TemplateResult } from "lit-html";
import { useLayoutEffect, useRef } from "Shared/haunted/CustomHooks";
import classNames from "classnames";
import { ref } from "Components/directives/ref";
import { useErrorMessage } from "Services/fluent-validator/useErrorMessage";
import { HauntedFunc } from "Shared/haunted/HooksHelpers";
import { useLabel } from "./searchbox/searchTab/flights/useLabel";

export const name = "ac-input";
export const useShadowDOM = false;

export interface Props {
	autoComplete: string;
	customClasses: string[];
	disablePaste?: boolean;
	errorMessage: string;
	errorMessageDataTestCode: string;
	getFocusOnRender: boolean;
	isDisabled: boolean;
	isInvalid: boolean;
	isLabelSmaller: boolean;
	isReadonly: boolean;
	isSmall?: boolean;
	label: string | TemplateResult;
	maxLength: string;
	name: string;
	placeholder: string;
	tabIndexAttr: string;
	testId: string;
	type: "text" | "password";
	value: string;
	onBlur?: (e: FocusEvent) => void;
	onInput: (value: string) => void;
	onKeyUp?: (e: KeyboardEvent) => void;
	sanitizer: (e: KeyboardEvent) => string;
}

export const Component: HauntedFunc<Props> = (host) => {
	const props: Props = {
		autoComplete: host.autoComplete || "off",
		customClasses: host.customClasses,
		disablePaste: host.disablePaste,
		errorMessage: host.errorMessage,
		errorMessageDataTestCode: host.errorMessageDataTestCode,
		getFocusOnRender: host.getFocusOnRender,
		isDisabled: host.isDisabled,
		isInvalid: host.isInvalid,
		isLabelSmaller: host.isLabelSmaller,
		isReadonly: host.isReadonly,
		isSmall: host.isSmall,
		label: host.label,
		maxLength: host.maxLength || "",
		name: host.name || "",
		placeholder: host.placeholder || "",
		tabIndexAttr: host.tabIndexAttr || "",
		testId: host.testId || "",
		type: host.type || "text",
		value: host.value || "",
		onBlur: host.onBlur,
		onInput: host.onInput,
		onKeyUp: host.onKeyUp,
		sanitizer: host.sanitizer,
	};

	const inputElem = useRef<HTMLInputElement>(undefined);
	const wasAutofilled = useRef<boolean>(false);

	const label = useLabel({
		isDirty: Boolean(props.value) || Boolean(props.placeholder),
		isDisabled: props.isDisabled,
		isReadOnly: props.isReadonly,
		label: props.label,
		isSmaller: props.isLabelSmaller,
	});

	const error = useErrorMessage({
		errorMessage: props.errorMessage,
		errorMessageDataTestCode: props.errorMessageDataTestCode,
	});

	// FIXME Feel free to do this nicer - this is to notify the input that it was autofilled
	// DEVNOTE Chrome does not trigger any event when autofilling an input
	const shouldHandleAutofill = () => {
		if (!inputElem.current) return false;

		const currentBackgroundColor = getComputedStyle(inputElem.current).backgroundColor;
		const nonAutofilledBackgrounds = ["rgb(255, 255, 255)", "rgb(249, 212, 216)"];

		return !nonAutofilledBackgrounds.includes(currentBackgroundColor) && inputElem.current?.value !== props.value;
	};

	const addFocusOnRender = () => {
		if (props.getFocusOnRender) window.setTimeout(() => inputElem.current?.focus(), 100);
	};

	const handleInput = (e: KeyboardEvent) => {
		inputElem.current.value = typeof props.sanitizer === "function" ? props.sanitizer(e) : inputElem.current?.value;
		props.onInput(inputElem.current?.value);
	};

	const handleKeyup = (e: KeyboardEvent) => {
		if (props.onKeyUp) props.onKeyUp(e);
	};

	const handleChromeAutofill = () => {
		if (wasAutofilled.current) return;

		if (shouldHandleAutofill()) {
			wasAutofilled.current = true;
			props.onInput(inputElem.current?.value);
		}
	};

	useLayoutEffect(() => {
		inputElem.current.value = props.value;
	}, [props.value]);

	useLayoutEffect(addFocusOnRender, []);

	useLayoutEffect(handleChromeAutofill);

	return html`
		<div class="relative w-full leading-none">
			<input
				autocomplete=${props.autoComplete}
				class=${classNames(props.customClasses, {
					"peer m-0 block w-full appearance-none rounded-lg border border-solid pb-[3px] pr-[4px] text-left text-n-blue common-transition":
						!props.customClasses,
					"placeholder:text-sm/none placeholder:text-[#818a91]": !props.customClasses,
					"focus:border-be-cyan focus:text-n-blue": !props.customClasses,
					"disabled": props.isDisabled,
					"pt-[21px]": props.label && !props.isSmall,
					"pt-[17px]": props.label && props.isSmall,
					"h-[52px] pl-4 text-[20px] leading-[52px]": !props.isSmall,
					"h-[34px] pl-4 text-[13px] leading-[34px] md:leading-[42px] md:h-[42px]": props.isSmall,
					"pt-[5px]": !props.label,
					"border-n-blue bg-white": !props.isInvalid,
					"border-[#e299a1] bg-[#f9d4d8]": props.isInvalid,
				})}
				data-test-id=${props.testId}
				name=${host.name}
				placeholder=${props.placeholder}
				?readonly=${props.isReadonly}
				ref=${ref(inputElem)}
				tabindex=${props.tabIndexAttr}
				type=${props.type}
				value=${props.value}
				maxlength=${props.maxLength}
				@blur=${props.onBlur}
				@input=${handleInput}
				@keyup=${handleKeyup}
				@paste=${props.disablePaste ? (e: Event) => e.preventDefault() : undefined}
			/>
			${label.htmlTemplate()} ${error.htmlTemplate()}
		</div>
	`;
};
